import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader, Music, Pencil, Trash2, X, Save, Image as ImageIcon, Video, Link as LinkIcon, Send, Plus, Check, Eye, Maximize2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { uploadInChunks } from "@/lib/chunkedUpload";
import FullscreenMediaViewer from "@/components/FullscreenMediaViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/);
  return match ? match[1] : null;
}

function PostMedia({ post, mini = false, onFullscreen }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const resolveUrl = (u) => (u && u.startsWith("/api")) ? `${backendUrl}${u}` : u;
  if (post.external_url) {
    const ytId = extractYouTubeId(post.external_url);
    if (ytId) {
      if (mini) return <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />;
      return (
        <div className="aspect-video rounded-2xl overflow-hidden bg-[#5B3E2E]/5 mt-3">
          <iframe src={`https://www.youtube.com/embed/${ytId}`} title="Video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      );
    }
    if (mini) return <div className="w-full h-full bg-[#E48D57]/10 flex items-center justify-center"><span className="text-[10px] text-[#E48D57]">Enlace</span></div>;
    return <a href={post.external_url} target="_blank" rel="noopener noreferrer" className="block mt-3 p-4 bg-[#5B3E2E]/5 rounded-2xl text-sm text-[#E48D57] hover:underline break-all">{post.external_url}</a>;
  }
  if (post.media_url && post.media_type === "image") {
    const src = resolveUrl(post.media_url);
    if (mini) return <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />;
    return (
      <button
        type="button"
        onClick={() => onFullscreen?.({ type: "image", src })}
        className="relative mt-3 w-full group block"
        aria-label="Ver imagen a pantalla completa"
      >
        <img src={src} alt="" className="w-full rounded-2xl object-contain max-h-[75vh] bg-black/5" loading="lazy" />
        <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity sm:opacity-80">
          <Maximize2 size={16} />
        </span>
      </button>
    );
  }
  if (post.media_url && post.media_type === "video" && post.processing) {
    return (
      <div data-testid={`post-video-processing-${post.id}`} className={`${mini ? "w-full h-full" : "mt-3 w-full aspect-video rounded-2xl"} bg-[#5B3E2E]/8 flex flex-col items-center justify-center gap-2 text-center px-4`}>
        <Loader size={mini ? 18 : 24} className="animate-spin text-[#E48D57]" />
        <p className={mini ? "text-[11px] text-[#5B3E2E]/70" : "text-sm text-[#5B3E2E]/70"}>Procesando vídeo…<br /><span className={mini ? "text-[10px] text-[#5B3E2E]/50" : "text-xs text-[#5B3E2E]/50"}>Estará listo en unos minutos</span></p>
      </div>
    );
  }
  if (post.media_url && post.media_type === "video") {
    const src = resolveUrl(post.media_url);
    const posterSrc = resolveUrl(post.thumbnail_url);
    if (mini) {
      // Show custom thumbnail (if any) with a play overlay; fallback to grey tile with play icon
      if (posterSrc) {
        return (
          <div className="relative w-full h-full">
            <img src={posterSrc} alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/15">
              <div className="w-10 h-10 rounded-full bg-white/85 flex items-center justify-center shadow-md">
                <div className="w-0 h-0 border-l-[10px] border-l-[#E48D57] border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent ml-1" />
              </div>
            </div>
          </div>
        );
      }
      return <div className="w-full h-full bg-[#5B3E2E]/8 flex items-center justify-center"><div className="w-10 h-10 rounded-full bg-[#E48D57]/20 flex items-center justify-center"><div className="w-0 h-0 border-l-[10px] border-l-[#E48D57] border-t-[7px] border-t-transparent border-b-[7px] border-b-transparent ml-1" /></div></div>;
    }
    return (
      <button
        type="button"
        onClick={() => onFullscreen?.({ type: "video", src, poster: posterSrc })}
        className="relative mt-3 w-full group block"
        aria-label="Ver vídeo a pantalla completa"
      >
        {posterSrc ? (
          <img src={posterSrc} alt="" className="w-full rounded-2xl object-contain max-h-[75vh] bg-black" loading="lazy" />
        ) : (
          <div className="w-full aspect-video rounded-2xl bg-black" />
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
            <span className="w-0 h-0 border-l-[18px] border-l-[#C44A3A] border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1.5" />
          </span>
        </span>
      </button>
    );
  }
  if (post.media_url && post.media_type === "audio") {
    const src = resolveUrl(post.media_url);
    if (mini) return <div className="w-full h-full bg-[#6B9070]/8 flex items-center justify-center"><Music size={16} className="text-[#6B9070]/40" /></div>;
    return <audio controls className="w-full mt-3" src={src} />;
  }
  return null;
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Ahora";
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Ayer";
  return `Hace ${days} días`;
}

/* --- Video frame extraction (cliente) --- */
async function extractVideoFrames(file, count = 3) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    const cleanup = () => URL.revokeObjectURL(url);
    const frames = [];
    let timeoutId;
    video.onerror = () => { cleanup(); reject(new Error("No se pudo cargar el vídeo")); };
    video.onloadedmetadata = async () => {
      const duration = video.duration;
      if (!duration || !isFinite(duration) || duration < 0.2) {
        cleanup();
        reject(new Error("Duración del vídeo desconocida"));
        return;
      }
      const points = Array.from({ length: count }, (_, i) =>
        duration * (0.15 + (0.7 * i) / Math.max(1, count - 1))
      );
      const w = video.videoWidth || 320;
      const h = video.videoHeight || 320;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      for (const t of points) {
        try {
          await new Promise((res, rej) => {
            const onSeeked = () => { video.removeEventListener("seeked", onSeeked); clearTimeout(timeoutId); res(); };
            video.addEventListener("seeked", onSeeked, { once: true });
            timeoutId = setTimeout(() => { video.removeEventListener("seeked", onSeeked); rej(new Error("Timeout")); }, 6000);
            video.currentTime = Math.min(t, duration - 0.1);
          });
          ctx.drawImage(video, 0, 0, w, h);
          const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
          const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
          if (blob) frames.push({ time: t, blob, dataUrl });
        } catch { /* ignora frame puntual fallido */ }
      }
      cleanup();
      resolve(frames);
    };
  });
}

/* Create new post modal (admin only) */
function CreatePostModal({ onClose, onCreated }) {
  const [content, setContent] = useState("");
  const [mediaSource, setMediaSource] = useState("none");  // "none" | "file" | "url"
  const [externalUrl, setExternalUrl] = useState("");
  const [file, setFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [suggestedFrames, setSuggestedFrames] = useState([]);   // [{ blob, dataUrl }]
  const [extractingFrames, setExtractingFrames] = useState(false);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0..1 during upload
  const [mode, setMode] = useState("edit");  // "edit" | "preview"

  const isVideoFile = file && (file.type || "").startsWith("video/");

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    // Al cambiar el vídeo, resetea portada previa
    setThumbFile(null);
    setThumbPreview(null);
    setSuggestedFrames([]);
    setSelectedFrameIndex(-1);
    if (f) {
      setMediaSource("file");
      setExternalUrl("");
    }
  };

  // Auto-extract 3 frames cuando cambia a un archivo de vídeo
  useEffect(() => {
    if (!isVideoFile) { setSuggestedFrames([]); return; }
    let cancelled = false;
    setExtractingFrames(true);
    extractVideoFrames(file, 3)
      .then(frames => { if (!cancelled) setSuggestedFrames(frames); })
      .catch(() => { if (!cancelled) setSuggestedFrames([]); })
      .finally(() => { if (!cancelled) setExtractingFrames(false); });
    return () => { cancelled = true; };
  }, [file, isVideoFile]);

  const pickSuggestedFrame = (idx) => {
    const frame = suggestedFrames[idx];
    if (!frame) return;
    const framedFile = new File([frame.blob], `cover-frame-${idx}.jpg`, { type: "image/jpeg" });
    setThumbFile(framedFile);
    setThumbPreview(frame.dataUrl);
    setSelectedFrameIndex(idx);
  };

  const handleThumbChange = (e) => {
    const f = e.target.files?.[0] || null;
    setThumbFile(f);
    setSelectedFrameIndex(-1);  // reset selección de frame sugerido
    if (f) {
      const reader = new FileReader();
      reader.onload = (ev) => setThumbPreview(ev.target?.result || null);
      reader.readAsDataURL(f);
    } else {
      setThumbPreview(null);
    }
  };

  const handleUrlChange = (e) => {
    const v = e.target.value;
    setExternalUrl(v);
    if (v.trim()) {
      setMediaSource("url");
      setFile(null);
      setThumbFile(null);
      setThumbPreview(null);
    }
  };

  const switchSource = (src) => {
    setMediaSource(src);
    if (src === "file") {
      setExternalUrl("");
    } else if (src === "url") {
      setFile(null);
      setThumbFile(null);
      setThumbPreview(null);
    } else {
      setFile(null);
      setExternalUrl("");
      setThumbFile(null);
      setThumbPreview(null);
    }
  };

  const canPublish = content.trim().length > 0 || (mediaSource === "file" && file) || (mediaSource === "url" && externalUrl.trim().length > 0);

  // Objeto de vista previa — usa un blob URL local para el archivo (sin subida)
  const previewPost = useMemo(() => {
    let media_url = null, media_type = null, external_url = null;
    if (mediaSource === "file" && file) {
      media_url = URL.createObjectURL(file);
      const t = file.type || "";
      media_type = t.startsWith("image/") ? "image" : t.startsWith("video/") ? "video" : t.startsWith("audio/") ? "audio" : null;
    } else if (mediaSource === "url" && externalUrl.trim()) {
      external_url = externalUrl.trim();
    }
    return {
      id: "preview",
      content: content.trim(),
      media_type,
      media_url,
      external_url,
      thumbnail_url: thumbPreview,
      author_name: "Refugium",
      created_at: new Date().toISOString(),
    };
  }, [content, mediaSource, file, externalUrl, thumbPreview]);

  // Revoca el blob URL cuando el previewPost cambia o se cierra el modal
  useEffect(() => {
    return () => {
      if (previewPost.media_url && previewPost.media_url.startsWith("blob:")) {
        URL.revokeObjectURL(previewPost.media_url);
      }
    };
  }, [previewPost.media_url]);

  const handleCreate = async () => {
    if (!canPublish) {
      toast.error("Añade texto, un archivo o un enlace");
      return;
    }
    setSaving(true);
    try {
      let mediaUrl = null;
      let finalMediaType = null;
      let finalExternalUrl = null;
      let finalThumbnailUrl = null;

      if (mediaSource === "file" && file) {
        setUploadProgress(0);
        const up = await uploadInChunks(file, "feed", (p) => setUploadProgress(p));
        mediaUrl = up.url;
        finalMediaType = up.media_type;
        // Si es vídeo y hay portada personalizada, súbela también (siempre pequeña)
        if (finalMediaType === "video" && thumbFile) {
          const thumbUp = await uploadInChunks(thumbFile, "feed");
          finalThumbnailUrl = thumbUp.url;
        }
      } else if (mediaSource === "url" && externalUrl.trim()) {
        finalExternalUrl = externalUrl.trim();
      }

      const payload = {
        content: content.trim(),
        media_type: finalMediaType,
        media_url: mediaUrl,
        external_url: finalExternalUrl,
        thumbnail_url: finalThumbnailUrl,
      };
      const { data: post } = await axios.post(`${API}/feed`, payload, { withCredentials: true });
      toast.success("Publicación creada");
      onCreated(post);
    } catch (err) {
      const status = err.response?.status;
      const serverDetail = err.response?.data?.detail;
      let msg;
      if (status === 413) {
        msg = "El archivo es demasiado grande. Baja la calidad del vídeo (720p) y vuelve a intentarlo.";
      } else if (status === 502 || status === 504) {
        msg = "La subida tardó demasiado. Prueba con un archivo más pequeño o con mejor conexión.";
      } else if (!err.response) {
        // Network error — likely proxy rejected the body or connection dropped
        msg = "No se pudo conectar con el servidor. Suele pasar con archivos muy grandes; prueba a bajar la calidad del vídeo (720p).";
      } else {
        msg = serverDetail || "Error al publicar";
      }
      toast.error(msg);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  return (
    <div
      data-testid="create-post-modal"
      className="fixed inset-0 z-[60] bg-[#5B3E2E]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="w-full sm:max-w-md bg-[#FDFAF4] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col"
        style={{ maxHeight: "min(88vh, 720px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between border-b border-[#5B3E2E]/8 flex-shrink-0">
          <h3 className="font-heading text-xl text-[#5B3E2E]">
            {mode === "preview" ? "Vista previa" : "Nueva publicación"}
          </h3>
          <button data-testid="create-post-close" onClick={() => !saving && onClose()} className="text-[#5B3E2E]/40 hover:text-[#5B3E2E] p-1">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content — preview mode */}
        {mode === "preview" && (
          <div className="p-5 overflow-y-auto flex-1 bg-[#F5EEDB]">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/50 font-semibold text-center mb-3">Así se verá en el feed</p>
            <HeroPost post={previewPost} onClick={() => {}} />
            <p className="text-[11px] text-[#5B3E2E]/50 text-center mt-4 leading-relaxed">
              Si algo no queda como quieres, vuelve a editar. Cuando estés list@, pulsa <strong>Publicar</strong>.
            </p>
          </div>
        )}

        {/* Scrollable content — edit mode */}
        <div className={`p-5 overflow-y-auto flex-1 space-y-5 ${mode === "preview" ? "hidden" : ""}`}>
          <div>
            <p className="text-xs text-[#5B3E2E]/70 mb-1.5 font-medium">Texto</p>
            <Textarea
              data-testid="create-post-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Escribe algo para la comunidad Refugium…"
              className="bg-white border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] min-h-[110px]"
              style={{ fontSize: "16px" }}
              maxLength={2000}
            />
          </div>

          {/* Media source picker (exclusive) */}
          <div>
            <p className="text-xs text-[#5B3E2E]/70 mb-2 font-medium">Adjunta contenido (opcional) — elige una opción</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                data-testid="media-source-none"
                onClick={() => switchSource("none")}
                type="button"
                className={`py-2 rounded-xl border-2 text-xs font-medium transition-all ${
                  mediaSource === "none"
                    ? "border-[#5B3E2E]/60 bg-[#5B3E2E]/8 text-[#5B3E2E]"
                    : "border-[#5B3E2E]/12 text-[#5B3E2E]/50 hover:border-[#5B3E2E]/25"
                }`}
              >
                Solo texto
              </button>
              <button
                data-testid="media-source-file"
                onClick={() => switchSource("file")}
                type="button"
                className={`py-2 rounded-xl border-2 text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  mediaSource === "file"
                    ? "border-[#E48D57] bg-[#E48D57]/10 text-[#E48D57]"
                    : "border-[#5B3E2E]/12 text-[#5B3E2E]/50 hover:border-[#5B3E2E]/25"
                }`}
              >
                <ImageIcon size={13} /> Archivo
              </button>
              <button
                data-testid="media-source-url"
                onClick={() => switchSource("url")}
                type="button"
                className={`py-2 rounded-xl border-2 text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  mediaSource === "url"
                    ? "border-[#6B9070] bg-[#6B9070]/10 text-[#6B9070]"
                    : "border-[#5B3E2E]/12 text-[#5B3E2E]/50 hover:border-[#5B3E2E]/25"
                }`}
              >
                <LinkIcon size={13} /> Enlace
              </button>
            </div>

            {mediaSource === "file" && (
              <div>
                <input
                  data-testid="create-post-file"
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-[#5B3E2E] file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[#E48D57]/10 file:text-[#E48D57] hover:file:bg-[#E48D57]/15"
                />
                {file && <p className="text-[11px] text-[#5B3E2E]/50 mt-1.5">{file.name} <span className="text-[#5B3E2E]/35">· {(file.size / 1024 / 1024).toFixed(1)} MB</span></p>}
                <p className="text-[10px] text-[#5B3E2E]/40 mt-1 leading-relaxed">
                  Los vídeos largos pueden tardar un poco en aparecer aquí mientras tu móvil los prepara (si están en iCloud, primero se descargan). Después se suben por trozos.
                </p>

                {/* Video cover picker — solo cuando el archivo es un vídeo */}
                {isVideoFile && (
                  <div className="mt-4 p-3 rounded-xl bg-[#E48D57]/5 border border-[#E48D57]/20">
                    <p className="text-xs text-[#5B3E2E] font-medium mb-1">Portada del vídeo</p>
                    <p className="text-[11px] text-[#5B3E2E]/55 mb-3">Elige una de las 3 sugerencias extraídas de tu vídeo, o sube una imagen propia.</p>

                    {/* Frames sugeridos automáticamente */}
                    {extractingFrames && (
                      <div className="flex items-center gap-2 mb-3 text-[11px] text-[#5B3E2E]/50">
                        <Loader size={12} className="animate-spin" />
                        <span>Extrayendo frames del vídeo…</span>
                      </div>
                    )}
                    {!extractingFrames && suggestedFrames.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#5B3E2E]/45 font-semibold mb-1.5">Sugerencias</p>
                        <div className="grid grid-cols-3 gap-2">
                          {suggestedFrames.map((frame, idx) => (
                            <button
                              key={idx}
                              type="button"
                              data-testid={`suggested-frame-${idx}`}
                              onClick={() => pickSuggestedFrame(idx)}
                              className={`relative aspect-square rounded-lg overflow-hidden bg-black/5 border-2 transition-all ${
                                selectedFrameIndex === idx
                                  ? "border-[#E48D57] shadow-[0_2px_10px_rgba(217,120,51,0.35)] scale-[1.02]"
                                  : "border-transparent hover:border-[#E48D57]/40"
                              }`}
                            >
                              <img src={frame.dataUrl} alt={`Frame ${idx + 1}`} className="w-full h-full object-cover" />
                              {selectedFrameIndex === idx && (
                                <div className="absolute inset-0 bg-[#E48D57]/15 flex items-center justify-center">
                                  <div className="w-6 h-6 rounded-full bg-[#E48D57] text-white flex items-center justify-center">
                                    <Check size={14} strokeWidth={3} />
                                  </div>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Alternativa manual */}
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[#5B3E2E]/45 font-semibold mb-1.5">O sube una imagen propia</p>
                    <input
                      data-testid="create-post-thumbnail"
                      type="file"
                      accept="image/*"
                      onChange={handleThumbChange}
                      className="block w-full text-sm text-[#5B3E2E] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[#5B3E2E]/8 file:text-[#5B3E2E] hover:file:bg-[#5B3E2E]/12"
                    />

                    {/* Preview de la portada elegida (frame o manual) */}
                    {thumbPreview && (
                      <div className="mt-3">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[#5B3E2E]/45 font-semibold mb-1.5">Portada seleccionada</p>
                        <div className="relative w-full max-w-[180px] aspect-square rounded-lg overflow-hidden bg-black/5">
                          <img src={thumbPreview} alt="Portada" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => { setThumbFile(null); setThumbPreview(null); setSelectedFrameIndex(-1); }}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs flex items-center justify-center hover:bg-black/80"
                            aria-label="Quitar portada"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {mediaSource === "url" && (
              <Input
                data-testid="create-post-url"
                value={externalUrl}
                onChange={handleUrlChange}
                placeholder="https://youtube.com/… o cualquier enlace"
                className="bg-white border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E]"
                style={{ fontSize: "16px" }}
              />
            )}
          </div>
        </div>

        {/* Sticky footer with the Publish button */}
        <div
          className="p-4 border-t border-[#5B3E2E]/10 bg-[#FDFAF4] flex-shrink-0"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          {mode === "edit" ? (
            <Button
              data-testid="create-post-preview-btn"
              onClick={() => {
                if (!canPublish) { toast.error("Añade texto, un archivo o un enlace"); return; }
                setMode("preview");
              }}
              disabled={saving || !canPublish}
              className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_14px_rgba(217,120,51,0.35)]"
            >
              <Eye size={16} />
              Vista previa
            </Button>
          ) : (
            <>
              <div className="flex gap-2">
                <Button
                  data-testid="create-post-back-btn"
                  variant="outline"
                  onClick={() => setMode("edit")}
                  disabled={saving}
                  className="flex-1 rounded-full border-[#5B3E2E]/20 text-[#5B3E2E] py-3.5 font-medium hover:bg-[#5B3E2E]/5"
                >
                  Volver a editar
                </Button>
                <Button
                  data-testid="create-post-submit"
                  onClick={handleCreate}
                  disabled={saving || !canPublish}
                  className="flex-1 bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_14px_rgba(217,120,51,0.35)]"
                >
                  {saving ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                  {saving
                    ? uploadProgress > 0 && uploadProgress < 1
                      ? `Subiendo ${Math.round(uploadProgress * 100)}%`
                      : "Publicando…"
                    : "Publicar"}
                </Button>
              </div>
              {saving && uploadProgress > 0 && uploadProgress < 1 && (
                <div data-testid="upload-progress" className="h-1 bg-[#5B3E2E]/10 rounded-full overflow-hidden mt-3">
                  <div
                    className="h-full bg-[#E48D57] transition-all duration-200"
                    style={{ width: `${Math.round(uploadProgress * 100)}%` }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* Edit post modal */
function EditPostModal({ post, onClose, onSaved }) {
  const [content, setContent] = useState(post?.content || "");
  const [externalUrl, setExternalUrl] = useState(post?.external_url || "");
  const [mediaType, setMediaType] = useState(post?.media_type || "none");
  const [mediaUrl, setMediaUrl] = useState(post?.media_url || "");
  const [file, setFile] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(post?.thumbnail_url || "");
  const [thumbFile, setThumbFile] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!post) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      let finalMediaUrl = mediaUrl;
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await axios.post(`${API}/feed/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        finalMediaUrl = data.url;
      }
      let finalThumbnailUrl = thumbnailUrl;
      if (thumbFile) {
        const thumbForm = new FormData();
        thumbForm.append("file", thumbFile);
        const { data: thumbUp } = await axios.post(`${API}/feed/upload`, thumbForm, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        finalThumbnailUrl = thumbUp.url;
      }
      const payload = {
        content: content.trim(),
        media_type: finalMediaUrl ? mediaType : null,
        media_url: finalMediaUrl || null,
        external_url: externalUrl || null,
        thumbnail_url: (finalMediaUrl && mediaType === "video") ? (finalThumbnailUrl || null) : null,
      };
      const { data } = await axios.put(`${API}/feed/${post.id}`, payload, { withCredentials: true });
      toast.success("Publicación actualizada");
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const removeMedia = () => { setMediaUrl(""); setFile(null); setMediaType("none"); setThumbnailUrl(""); setThumbFile(null); };
  const removeThumb = () => { setThumbnailUrl(""); setThumbFile(null); };
  const resolveUrl = (u) => (u && u.startsWith("/api")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFAF4] rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#5B3E2E]/8">
          <h3 className="font-heading text-lg text-[#5B3E2E]">Editar publicación</h3>
          <button data-testid="edit-post-close" onClick={onClose} className="text-[#5B3E2E]/40 hover:text-[#5B3E2E]"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Textarea
            data-testid="edit-post-content"
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Escribe tu publicación..."
            className="bg-transparent border-[#5B3E2E]/15 rounded-xl min-h-[110px] text-[#5B3E2E] text-sm"
          />
          {mediaUrl && !file && (
            <div className="relative rounded-xl overflow-hidden bg-[#5B3E2E]/5">
              {mediaType === "image" && <img src={mediaUrl.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${mediaUrl}` : mediaUrl} alt="" className="w-full max-h-60 object-cover" />}
              {mediaType === "video" && <video controls className="w-full max-h-60"><source src={mediaUrl.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${mediaUrl}` : mediaUrl} /></video>}
              {mediaType === "audio" && <audio controls className="w-full" src={mediaUrl.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${mediaUrl}` : mediaUrl} />}
              <button onClick={removeMedia} className="absolute top-2 right-2 w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80"><X size={14} /></button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {[
              { type: "image", icon: ImageIcon, label: "Imagen" },
              { type: "video", icon: Video, label: "Video" },
              { type: "audio", icon: Music, label: "Audio" },
            ].map(({ type, icon: Icon, label }) => (
              <button
                key={type}
                onClick={() => setMediaType(mediaType === type ? "none" : type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${mediaType === type ? "bg-[#E48D57] text-white" : "bg-[#5B3E2E]/5 text-[#5B3E2E]/60 hover:bg-[#5B3E2E]/10"}`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          {mediaType !== "none" && (
            <input type="file" accept={mediaType === "image" ? "image/*" : mediaType === "video" ? "video/*" : "audio/*"} onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm text-[#5B3E2E]/60 w-full" data-testid="edit-post-file" />
          )}

          {/* Portada del vídeo — sólo para media_type=video */}
          {mediaType === "video" && (mediaUrl || file) && (
            <div className="p-3 rounded-xl bg-[#E48D57]/5 border border-[#E48D57]/20">
              <p className="text-xs text-[#5B3E2E] font-medium mb-1">Portada del vídeo</p>
              <p className="text-[11px] text-[#5B3E2E]/55 mb-2">Imagen que se mostrará como miniatura en el feed.</p>
              {(thumbnailUrl || thumbFile) && (
                <div className="mb-2 relative w-full max-w-[160px] aspect-square rounded-lg overflow-hidden bg-black/5">
                  <img
                    src={thumbFile ? URL.createObjectURL(thumbFile) : resolveUrl(thumbnailUrl)}
                    alt="Portada"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={removeThumb}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                    aria-label="Quitar portada"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
              <input
                data-testid="edit-post-thumbnail"
                type="file"
                accept="image/*"
                onChange={e => setThumbFile(e.target.files?.[0] || null)}
                className="text-sm text-[#5B3E2E]/70 w-full"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <LinkIcon size={14} className="text-[#5B3E2E]/30" />
            <Input value={externalUrl} onChange={e => setExternalUrl(e.target.value)} placeholder="Enlace externo (YouTube, etc.)" className="bg-transparent border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] text-sm" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="flex-1 rounded-full border-[#5B3E2E]/15 text-[#5B3E2E]">Cancelar</Button>
            <Button data-testid="edit-post-save" onClick={handleSave} disabled={saving} className="flex-1 bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full flex items-center justify-center gap-1.5">
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Latest post - full size hero card */
function HeroPost({ post, onClick }) {
  const hasMedia = post.media_url || post.external_url;
  const hasContent = (post.content || "").trim().length > 0;
  return (
    <button
      data-testid="feed-post-hero"
      onClick={onClick}
      className="relative w-full text-left overflow-hidden rounded-3xl bg-[#FDFAF4] border border-[#5B3E2E]/8 shadow-[0_4px_20px_rgba(91,62,46,0.06)] hover:shadow-[0_10px_32px_rgba(91,62,46,0.12)] hover:-translate-y-0.5 transition-all"
    >
      {hasMedia ? (
        <div className="aspect-square bg-[#5B3E2E]/5 overflow-hidden">
          <PostMedia post={post} mini />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gradient-to-br from-[#E48D57]/10 via-[#FDFAF4] to-[#6B9070]/10 flex items-center justify-center p-8">
          <p className="text-[#5B3E2E] text-base leading-relaxed text-center line-clamp-6 font-medium">
            {post.content}
          </p>
        </div>
      )}
      {/* Texto como título/caption cuando hay media */}
      {hasMedia && hasContent && (
        <div className="px-4 py-3 border-t border-[#5B3E2E]/6">
          <p className="text-[#5B3E2E] text-[14px] leading-relaxed line-clamp-3 font-medium">
            {post.content}
          </p>
        </div>
      )}
      <div className="px-4 py-3 flex items-center justify-between border-t border-[#5B3E2E]/6">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="" className="w-7 h-7 rounded-full" />
          <div>
            <p className="text-[11px] font-semibold text-[#5B3E2E] font-heading leading-tight">{post.author_name || "Refugium"}</p>
            <p className="text-[10px] text-[#5B3E2E]/45 leading-tight">{timeAgo(post.created_at)}</p>
          </div>
        </div>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#E48D57] font-semibold">Nuevo</span>
      </div>
    </button>
  );
}

/* Previous post - mini card for grid */
function MiniPost({ post, onClick }) {
  const hasMedia = post.media_url || post.external_url;
  const hasContent = (post.content || "").trim().length > 0;
  return (
    <button data-testid="feed-post-mini" onClick={onClick} className="relative bg-[#FDFAF4] overflow-hidden text-left hover:opacity-90 transition-opacity w-full">
      <div className="aspect-square bg-[#5B3E2E]/5 overflow-hidden">
        {hasMedia ? (
          <PostMedia post={post} mini />
        ) : (
          <div className="w-full h-full flex items-center justify-center p-3">
            <p className="text-[11px] text-[#5B3E2E]/70 line-clamp-6 leading-snug text-center font-medium">{post.content}</p>
          </div>
        )}
      </div>
      {/* Franja de título superpuesta cuando hay media + texto */}
      {hasMedia && hasContent && (
        <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/75 via-black/50 to-transparent">
          <p className="text-white text-[10px] leading-snug line-clamp-2 font-medium">{post.content}</p>
        </div>
      )}
    </button>
  );
}

/* Expanded post modal */
function ExpandedPost({ post, onClose, isAdmin, onEdit, onDelete }) {
  const [fullscreen, setFullscreen] = useState(null); // { type, src, poster }
  if (!post) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-stretch sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-[#FDFAF4] rounded-none sm:rounded-3xl w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[95vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <img src={LOGO_URL} alt="Refugium" className="w-10 h-10 rounded-full shadow-sm" />
            <div className="flex-1">
              <p className="font-semibold text-[#5B3E2E] text-sm font-heading">{post.author_name || "Refugium"}</p>
              <p className="text-[11px] text-[#5B3E2E]/40">{timeAgo(post.created_at)}{post.edited_at && " · editado"}</p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 mr-1">
                <button data-testid={`edit-post-${post.id}`} onClick={() => onEdit(post)} className="p-2 rounded-full text-[#5B3E2E]/40 hover:text-[#E48D57] hover:bg-[#E48D57]/10 transition-colors" title="Editar"><Pencil size={15} /></button>
                <button data-testid={`delete-post-${post.id}`} onClick={() => onDelete(post)} className="p-2 rounded-full text-[#5B3E2E]/40 hover:text-[#D9534F] hover:bg-[#D9534F]/10 transition-colors" title="Eliminar"><Trash2 size={15} /></button>
              </div>
            )}
            <button data-testid="expanded-post-close" onClick={onClose} className="w-9 h-9 rounded-full bg-[#5B3E2E]/8 hover:bg-[#5B3E2E]/15 text-[#5B3E2E]/70 hover:text-[#5B3E2E] flex items-center justify-center transition-colors">
              <X size={18} />
            </button>
          </div>
          <PostMedia post={post} onFullscreen={setFullscreen} />
          {post.content && (
            <p className="mt-4 text-[#5B3E2E]/85 text-[16px] leading-relaxed whitespace-pre-wrap">{post.content}</p>
          )}
        </div>
      </div>
      <FullscreenMediaViewer
        open={!!fullscreen}
        mediaType={fullscreen?.type}
        src={fullscreen?.src}
        poster={fullscreen?.poster}
        onClose={() => setFullscreen(null)}
      />
    </div>
  );
}


export default function FeedPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPost, setExpandedPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/feed`, { withCredentials: true });
      setPosts(data);
    } catch (err) {
      console.error("Error loading feed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  // Mientras haya vídeos procesándose, refresca el feed cada 20 s
  const hasProcessing = posts.some(p => p.processing);
  useEffect(() => {
    if (!hasProcessing) return;
    const t = setInterval(fetchPosts, 20000);
    return () => clearInterval(t);
  }, [hasProcessing]);

  const handleEdit = (post) => { setEditingPost(post); setExpandedPost(null); };
  const handleSaved = (updated) => {
    setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
    setEditingPost(null);
  };
  const handleDelete = async (post) => {
    if (!window.confirm(`¿Eliminar esta publicación? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/feed/${post.id}`, { withCredentials: true });
      setPosts(prev => prev.filter(p => p.id !== post.id));
      setExpandedPost(null);
      toast.success("Publicación eliminada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div data-testid="feed-page" className="max-w-lg mx-auto px-4 py-4 relative">
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader size={24} className="text-[#E48D57] animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-full bg-[#E48D57]/8 flex items-center justify-center mx-auto mb-4">
            <img src={LOGO_URL} alt="" className="w-10 h-10 rounded-full" />
          </div>
          <p className="text-[#5B3E2E]/50 text-sm font-medium font-heading">Todavía no hay publicaciones</p>
          <p className="text-[#5B3E2E]/30 text-xs mt-1">Las publicaciones de Refugium aparecerán aquí</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Hero — última publicación en grande */}
          <div className="animate-slide-up" style={{ animationDelay: "0s", opacity: 0, animationFillMode: "forwards" }}>
            <HeroPost post={posts[0]} onClick={() => setExpandedPost(posts[0])} />
          </div>

          {posts.length > 1 && (
            <>
              {/* Divisor con leyenda */}
              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-[#5B3E2E]/10" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#5B3E2E]/40 font-semibold">Anteriores</span>
                <div className="flex-1 h-px bg-[#5B3E2E]/10" />
              </div>

              {/* Grid pequeño — resto de publicaciones */}
              <div className="grid grid-cols-3 gap-[3px]">
                {posts.slice(1).map((post, i) => (
                  <div key={post.id} className="animate-slide-up" style={{ animationDelay: `${(i + 1) * 0.03}s`, opacity: 0, animationFillMode: "forwards" }}>
                    <MiniPost post={post} onClick={() => setExpandedPost(post)} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Expanded post modal */}
      <ExpandedPost post={expandedPost} onClose={() => setExpandedPost(null)} isAdmin={isAdmin} onEdit={handleEdit} onDelete={handleDelete} />

      {/* Edit modal */}
      {editingPost && <EditPostModal post={editingPost} onClose={() => setEditingPost(null)} onSaved={handleSaved} />}

      {/* Create post modal (admin only) */}
      {creating && (
        <CreatePostModal
          onClose={() => setCreating(false)}
          onCreated={(newPost) => {
            setPosts(prev => [newPost, ...prev]);
            setCreating(false);
          }}
        />
      )}

      {/* Floating "+" button — only for admin */}
      {isAdmin && (
        <button
          data-testid="feed-create-post-btn"
          onClick={() => setCreating(true)}
          aria-label="Publicar nueva entrada"
          title="Publicar nueva entrada"
          className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-full bg-[#E48D57] text-white flex items-center justify-center shadow-[0_6px_20px_rgba(217,120,51,0.45)] hover:shadow-[0_10px_30px_rgba(217,120,51,0.55)] hover:-translate-y-0.5 active:scale-95 transition-all"
        >
          <Plus size={26} strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
}
